import cn from 'classnames';
import React, { useCallback } from 'react';

import { Api } from '@.api';
import { Layout } from '@.layout';
import { Table2 } from '@.partials';
import { Checkbox, Filter3 } from '@.ui';
import { Auth, LinkHelper, Tools } from '@/Services';
import { StationListResponse } from '@/Services/api/methods';
import { useModalTableConfirm } from '@/Shared/lib/useModalTableConfirm';
import { PermissionProps, Request } from '@/Types';

export const Stations: React.FC<PermissionProps> = (props) => {
    const auth = Auth.Use();

    const get = useCallback((q?: Request.All) => Api.stations().getAll(q), []);

    const modalTableArchiveConfirm = useModalTableConfirm({
        message: 'Do you really want to archive this station?',
        confirm: (id: number) => Api.stations().delete(id),
    });

    const modalTableRestoreConfirm = useModalTableConfirm({
        message: 'Do you really want to restore this station?',
        confirm: (id: number) => Api.stations().restore(id),
    });

    const getData = useCallback((d: StationListResponse) => d.stations, []);

    const item = useCallback((item: StationListResponse['stations'][number]) => ({ className: cn({ deleted: item.deleted_at }) }), []);

    return (
        <Layout name="stations" breadcrumbs={[{ name: 'Stations', current: true }]}>
            {modalTableArchiveConfirm.modal}
            {modalTableRestoreConfirm.modal}

            <h1>iRomaScents Stations</h1>
            <Table2 get={get} getData={getData} export="stations" item={item}>
                {{
                    filters: (handleChange) => (
                        <Filter3 onChange={handleChange}>
                            <Filter3.Search />
                            <Filter3.Companies related localSearch />
                            <Filter3.StoreGroups related localSearch />
                            <Filter3.Stores related localSearch />
                            <Filter3.Boolean placeholder="Archived" name="is_archived" />
                            <Filter3.Boolean placeholder="Updated" name="is_updated" />
                        </Filter3>
                    ),
                    items: [
                        (item) => ({
                            name: 'ID',
                            max: 15,
                            content: item?.identifier,
                            sort: 'identifier',
                        }),
                        (item) => ({
                            name: 'Name',
                            max: 15,
                            content: item?.name,
                            sort: 'name',
                            link: LinkHelper.iRomaScentsStationId(item.id),
                            disabled: !!item.deleted_at || !props.permissions?.update,
                        }),
                        (item) => ({
                            name: 'Last Online',
                            content: item.last_online_at ? Tools.date.autoFormat(item.last_online_at * 1000) : '',
                            sort: 'last_online_at',
                        }),
                        (item) => ({
                            name: 'Inactive time',
                            content: item.inactive_time ? Tools.time.format(item.inactive_time) : '',
                            sort: 'inactive_time',
                        }),
                        (item) => ({
                            name: 'Currency',
                            content: item?.currency?.name,
                            sort: 'currency',
                        }),
                        (item) => ({
                            name: 'Update',
                            content: <Checkbox isDisabled value={item.is_updated} />,
                            sort: 'is_updated',
                        }),
                    ],
                    actions: [
                        (item) => ({
                            type: 'edit',
                            link: LinkHelper.iRomaScentsStationsEditId(item.id),
                            disabled: !auth?.hasRights('Stations', 'update'),
                        }),
                        (item) => ({
                            type: item.deleted_at ? 'restore' : 'archive',
                            onClick: item.deleted_at ? () => modalTableRestoreConfirm.action(item.id) : () => modalTableArchiveConfirm.action(item.id),
                            disabled: !props.permissions?.delete,
                        }),
                        (item) => ({
                            type: 'open',
                            link: LinkHelper.iRomaScentsStationId(item.id),
                        }),
                    ],
                }}
            </Table2>
        </Layout>
    );
};
